/**
 * The block a guard resolved, waiting to be rendered where the customer was going.
 *
 * ### Why a store and not a redirect
 *
 * S-20 is labelled "TIPAR · NU ESTE UN SINGUR ECRAN" in the prototype. The block is
 * a pattern rendered **in place** — same URL, same breadcrumbs, same page measure —
 * not a destination. A customer who deep-links `/migrare/plata` while their company
 * is inactive should be looking at `/migrare/plata` with the block in the page, not
 * at some `/blocat` route that throws away where they were trying to get to.
 *
 * Angular guards cannot render. So the guard writes the block here, returns `true`,
 * and `orange-shell` renders the block instead of the router outlet. The screen
 * component is never created, which matters: a blocked payment screen must not fire
 * the payment screen's queries.
 *
 * ### Why the URL is part of the record
 *
 * Because the alternative is stale state. S-01 and S-02 carry no session guard —
 * S-01 renders its own block, since the eligibility chain's own loading state is
 * the thing it exists to show — so nothing would clear a block left behind by an
 * earlier navigation, and the shell would hide S-01 behind the previous screen's
 * dead end. Storing the URL the block applies to makes the question "is this block
 * for the page I am looking at" answerable rather than assumed.
 */
import { Injectable, signal } from '@angular/core';
import type { BlockPartsFragment } from '../../../generated/graphql';

interface AccessBlock {
  /** The URL the guard was asked to activate, exactly as the router spelled it. */
  readonly url: string;
  readonly block: BlockPartsFragment;
}

@Injectable({ providedIn: 'root' })
export class AccessBlockStore {
  private readonly current = signal<AccessBlock | null>(null);

  /** Set by a guard that resolved a block for `url`. */
  blockAt(url: string, block: BlockPartsFragment): void {
    this.current.set({ url, block });
  }

  clear(): void {
    this.current.set(null);
  }

  /** The block for `url`, or null. The shell asks this on every navigation. */
  blockFor(url: string): BlockPartsFragment | null {
    const held = this.current();
    return held !== null && held.url === url ? held.block : null;
  }

  /**
   * Whether a block is already standing for this URL.
   *
   * `draftGuard` asks before doing any work of its own: guards in a `canActivate`
   * array all run when the earlier ones return `true`, and a blocked customer being
   * redirected somewhere else by the next guard in line would replace an explanation
   * with a silent bounce.
   */
  isBlockedAt(url: string): boolean {
    return this.blockFor(url) !== null;
  }
}
